/**
 * IDDLAIChatAgent — Phase 6 · Agentic AI Interaction renderer.
 *
 * The candidate briefs a scripted AI agent, watches it plan and
 * execute (deterministic events), then approves, revises, or aborts
 * the deliverable. Observable competency is AGENTIC TASK FRAMING +
 * supervised handover.
 *
 * Scenario schema (comes from concept.scenario_text — no domain
 * content lives in this file):
 *
 *   {
 *     task_brief: string,
 *     agent_profile: { name, role, tools: [string] },
 *     scripted_agent_run: [
 *       { step: number, action: "plan"|"call_tool"|"compose"|"self_review",
 *         tool?: string, summary: string }
 *     ],
 *     agent_deliverable: { title: string, flag_review_of?: [number] }
 *   }
 *
 * Candidate-attributed events (actor="candidate"):
 *   chat_agent_opened          — mount
 *   task_framed                — { framing_text }
 *   agent_run_started          — reveal scripted run
 *   agent_step_reviewed        — { step_id }
 *   agent_step_flagged         — { step_id, reason }
 *   agent_revision_requested   — { note }
 *   agent_deliverable_reviewed — mounts the deliverable view
 *   final_decision             — { decision: "accept_deliverable"|"request_revision"|"abort_run" }
 *
 * Agent-attributed events (actor="agent") — pre-scripted so every
 * candidate sees identical opportunities:
 *   agent_step_emitted         — { step, action, tool?, summary }
 */
import { useEffect, useRef, useState } from "react";
import { Bot, Play, ClipboardCheck, Flag, FileText, CheckCircle2, RefreshCcw, XOctagon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function IDDLAIChatAgent({
  attemptId,
  conceptId,
  scenarioText,
  environment = "agentic_ai",
  onEventLogged,
}) {
  const taskBrief = scenarioText?.task_brief || "Brief the agent, watch it run, and decide.";
  const profile = scenarioText?.agent_profile || {};
  const scriptedRun = scenarioText?.scripted_agent_run || [];
  const deliverable = scenarioText?.agent_deliverable || null;

  const [framingText, setFramingText] = useState("");
  const [framed, setFramed] = useState(false);
  const [runStarted, setRunStarted] = useState(false);
  const [reviewedSteps, setReviewedSteps] = useState(new Set());
  const [flaggedSteps, setFlaggedSteps] = useState(new Set());
  const [deliverableSeen, setDeliverableSeen] = useState(false);
  const [finalized, setFinalized] = useState(null);
  const [busy, setBusy] = useState(false);
  const openedRef = useRef(false);

  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    emit("chat_agent_opened", null, "agent_home", "candidate");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function emit(eventName, eventValue, screenContext, actor = "candidate") {
    try {
      const r = await api.post(`/attempts/${attemptId}/events`, {
        concept_id: conceptId,
        environment,
        event_name: eventName,
        event_value: eventValue,
        screen_context: screenContext,
        actor,
        client_ts_ms: Date.now(),
      }).then((x) => x.data);
      if (onEventLogged && r?.event) onEventLogged(r.event);
      return r?.event;
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Event failed";
      toast.error(`Event ${eventName} rejected: ${detail}`);
      return null;
    }
  }

  const submitFraming = async () => {
    if (!framingText.trim() || framed || busy) return;
    setBusy(true);
    try {
      await emit("task_framed", { framing_text: framingText.trim() }, "framing", "candidate");
      setFramed(true);
    } finally { setBusy(false); }
  };

  const runAgent = async () => {
    if (runStarted || busy || !framed) return;
    setBusy(true);
    try {
      await emit("agent_run_started", null, "run", "candidate");
      setRunStarted(true);
      // Emit each scripted step as an agent-attributed event so the
      // score engine has a deterministic agent trace to reason about.
      for (const step of scriptedRun) {
        await emit("agent_step_emitted", {
          step: step.step, action: step.action,
          tool: step.tool || null, summary: step.summary,
        }, "run", "agent");
      }
    } finally { setBusy(false); }
  };

  const reviewStep = async (step) => {
    if (reviewedSteps.has(step.step)) return;
    await emit("agent_step_reviewed", { step_id: step.step }, "run", "candidate");
    setReviewedSteps((s) => new Set([...s, step.step]));
  };

  const flagStep = async (step) => {
    if (flaggedSteps.has(step.step)) return;
    await emit("agent_step_flagged", { step_id: step.step, reason: "attention_required" }, "run", "candidate");
    setFlaggedSteps((s) => new Set([...s, step.step]));
  };

  const openDeliverable = async () => {
    if (deliverableSeen) return;
    await emit("agent_deliverable_reviewed", null, "deliverable", "candidate");
    setDeliverableSeen(true);
  };

  const requestRevision = async () => {
    await emit("agent_revision_requested", { note: "revise" }, "run", "candidate");
  };

  const finalize = async (decision) => {
    if (busy || finalized) return;
    setBusy(true);
    try {
      await emit("final_decision", {
        decision, reviewed_count: reviewedSteps.size, flagged_count: flaggedSteps.size,
      }, "finalize", "candidate");
      setFinalized(decision);
      toast.success(`Recorded: ${decision}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="iddl-ai-chat-agent">
      {/* Left: brief + framing + finalize */}
      <section className="border border-[var(--iddl-rule)] bg-white">
        <header className="px-3 py-2 border-b border-[var(--iddl-rule)] flex items-center gap-2 text-sm">
          <Bot size={14} />
          <span className="font-display font-semibold">Task briefing</span>
        </header>
        <div className="p-3 space-y-3">
          <p className="text-sm leading-6 whitespace-pre-wrap" data-testid="chat-agent-task-brief">{taskBrief}</p>
          <div className="text-xs text-zinc-600 space-y-0.5">
            <div><strong>Agent:</strong> {profile.name || "—"} · <em>{profile.role || ""}</em></div>
            <div><strong>Tools:</strong> {(profile.tools || []).join(", ") || "—"}</div>
          </div>

          <div className="space-y-2 border-t border-[var(--iddl-rule)] pt-3">
            <label className="label-mono text-xs" htmlFor="chat-agent-framing">Frame the task for the agent</label>
            <textarea
              id="chat-agent-framing"
              rows={4}
              className="w-full border border-[var(--iddl-rule)] px-2 py-1 text-sm"
              placeholder="e.g. 'Draft the 5-slide monthly briefing focused on incidents ENG-4821 and ENG-4907; label anything unverified as a hypothesis.'"
              value={framingText} onChange={(e) => setFramingText(e.target.value)}
              disabled={framed}
              data-testid="chat-agent-framing-input"
            />
            <Button size="sm" variant="outline" className="rounded-none"
              onClick={submitFraming} disabled={framed || !framingText.trim() || busy}
              data-testid="chat-agent-submit-framing">
              <ClipboardCheck size={14} className="me-1" /> Submit framing
            </Button>
          </div>

          <div className="space-y-2 border-t border-[var(--iddl-rule)] pt-3">
            <Button size="sm" variant="outline" className="rounded-none"
              onClick={runAgent} disabled={!framed || runStarted || busy}
              data-testid="chat-agent-run-agent">
              <Play size={14} className="me-1" /> Run the agent
            </Button>
          </div>

          {runStarted && (
            <div className="space-y-2 border-t border-[var(--iddl-rule)] pt-3">
              <div className="label-mono text-xs">Final decision</div>
              <div className="grid grid-cols-1 gap-2">
                <Button size="sm" variant="outline" disabled={busy || !!finalized || !deliverableSeen}
                  onClick={() => finalize("accept_deliverable")}
                  data-testid="chat-agent-final-accept">
                  <CheckCircle2 size={14} className="me-1" /> Accept deliverable
                </Button>
                <Button size="sm" variant="outline" disabled={busy || !!finalized || !deliverableSeen}
                  onClick={async () => { await requestRevision(); await finalize("request_revision"); }}
                  data-testid="chat-agent-final-revise">
                  <RefreshCcw size={14} className="me-1" /> Request revision
                </Button>
                <Button size="sm" variant="outline" disabled={busy || !!finalized}
                  onClick={() => finalize("abort_run")}
                  data-testid="chat-agent-final-abort">
                  <XOctagon size={14} className="me-1" /> Abort run
                </Button>
              </div>
              {finalized ? (
                <div className="text-xs text-emerald-800 pt-2">
                  Recorded: <strong>{finalized}</strong>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </section>

      {/* Right: scripted agent run + deliverable */}
      <section className="border border-[var(--iddl-rule)] bg-white" data-testid="chat-agent-run-panel">
        <header className="px-3 py-2 border-b border-[var(--iddl-rule)] flex items-center gap-2 text-sm">
          <Play size={14} />
          <span className="font-display font-semibold">Agent execution trace</span>
          <span className="ms-auto label-mono text-xs">
            {reviewedSteps.size}/{scriptedRun.length} reviewed
          </span>
        </header>
        <div className="p-3 space-y-2">
          {!runStarted && (
            <div className="text-xs text-zinc-500 italic">
              Submit framing on the left, then click <em>Run the agent</em> to reveal the scripted plan and execution steps.
            </div>
          )}
          {runStarted && scriptedRun.map((step) => {
            const reviewed = reviewedSteps.has(step.step);
            const flagged = flaggedSteps.has(step.step);
            return (
              <div key={step.step}
                className={`border ${flagged ? "border-rose-400 bg-rose-50" : reviewed ? "border-emerald-300 bg-emerald-50/40" : "border-[var(--iddl-rule)]"} p-2`}
                data-testid={`chat-agent-step-${step.step}`}>
                <div className="flex items-center gap-2 text-xs">
                  <span className="label-mono">step {step.step}</span>
                  <code className="font-mono font-semibold">{step.action}</code>
                  {step.tool ? <span className="label-mono">tool: {step.tool}</span> : null}
                </div>
                <div className="text-sm mt-1">{step.summary}</div>
                <div className="flex gap-2 mt-2">
                  <button type="button"
                    onClick={() => reviewStep(step)}
                    disabled={reviewed}
                    className="text-xs px-2 py-1 border border-emerald-400 disabled:opacity-50"
                    data-testid={`chat-agent-step-review-${step.step}`}>
                    {reviewed ? "Reviewed" : "Mark reviewed"}
                  </button>
                  <button type="button"
                    onClick={() => flagStep(step)}
                    disabled={flagged}
                    className="text-xs px-2 py-1 border border-rose-400 disabled:opacity-50"
                    data-testid={`chat-agent-step-flag-${step.step}`}>
                    <Flag size={10} className="inline me-1" /> {flagged ? "Flagged" : "Flag concern"}
                  </button>
                </div>
              </div>
            );
          })}

          {runStarted && deliverable && (
            <div className="border border-[var(--iddl-rule)] p-2 mt-3">
              <div className="text-xs label-mono">Deliverable</div>
              <div className="text-sm font-display font-semibold mt-1">{deliverable.title}</div>
              <button type="button"
                onClick={openDeliverable}
                disabled={deliverableSeen}
                className="mt-2 text-xs px-2 py-1 border border-[var(--iddl-rule)]"
                data-testid="chat-agent-open-deliverable">
                <FileText size={10} className="inline me-1" />
                {deliverableSeen ? "Deliverable reviewed" : "Open deliverable"}
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
